import { useMemo, useState, type ReactNode } from 'react';
import { Icon, V2Button } from '@sakhtar/design-system/react';
import { AssistantMenu } from './AssistantMenu.js';
import { RowMenu } from '../../../components/list/RowMenu.js';
import type { AiTokens } from '../aiPreview.js';
import { faInt } from './format.js';
import { SAMPLE_LIST_NOTE } from './sample.js';
import type { ConversationSummary } from './types.js';

export interface ConversationRailProps {
  readonly conversations: readonly ConversationSummary[];
  readonly activeId: string | null;
  readonly onSelect: (id: string) => void;
  readonly onNew: () => void;
  readonly onTogglePin: (id: string) => void;
  readonly onRemove: (id: string) => void;
  readonly tokens: AiTokens;
  /** Defaults to the preview token labels. */
  readonly balanceLabel?: string;
  readonly balanceHint?: ReactNode;
  readonly walletHref: string;
  /**
   * Mobile list page (M1 / M4): a notice shown above the list, e.g. the
   * out-of-tokens bar. Rendered by the parent on mobile only.
   */
  readonly notice?: ReactNode;
  /** Out of tokens (M4): history stays readable, each row says «خواندنی». */
  readonly readOnly?: boolean;
  /** Live customer mode: hides sample badge and sample notes. */
  readonly isLive?: boolean;
  /** Tenant base path for the assistant menu links. */
  readonly menuBase: string;
  readonly isTenantAdmin: boolean;
  /** Shows the pulsing red dot: the assistant is not set up for this tenant yet. */
  readonly needsSetup?: boolean;
}

const GROUPS: readonly {
  readonly id: string;
  readonly label: string;
  readonly match: (c: ConversationSummary) => boolean;
}[] = [
  { id: 'pinned', label: 'سنجاق‌شده', match: (c) => c.pinned },
  { id: 'today', label: 'امروز', match: (c) => !c.pinned && c.bucket === 'today' },
  { id: 'week', label: '۷ روز گذشته', match: (c) => !c.pinned && c.bucket === 'week' },
];

/** Left-hand (start) column: search, «گفتگوی جدید», grouped history, token balance box. */
export function ConversationRail({
  conversations,
  activeId,
  onSelect,
  onNew,
  onTogglePin,
  onRemove,
  tokens,
  balanceLabel,
  notice,
  readOnly = false,
  isLive = false,
  menuBase,
  isTenantAdmin,
  needsSetup = false,
}: ConversationRailProps) {
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim();
  const visible = useMemo(
    () =>
      normalizedQuery === ''
        ? conversations
        : conversations.filter((c) => c.title.includes(normalizedQuery)),
    [conversations, normalizedQuery],
  );

  return (
    <aside className="ai-rail" aria-label="گفتگوها" data-readonly={readOnly}>
      {notice ? <div className="ai-rail__notice">{notice}</div> : null}
      <div className="ai-rail__top">
        <V2Button
          variant="primary"
          icon="plus"
          className="sk-btn--block ai-rail__new"
          onClick={onNew}
        >
          گفتگوی جدید
        </V2Button>
        <span className="sk-input-group ai-rail__search">
          <Icon name="search" size="sm" />
          <input
            type="search"
            placeholder="جستجو در گفتگوها"
            aria-label="جستجو در گفتگوها"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </span>
      </div>

      <div className="ai-rail__list">
        {!isLive ? (
          <div className="ai-rail__note">
            <span className="sk-tag">{SAMPLE_LIST_NOTE}</span>
          </div>
        ) : null}
        {visible.length === 0 ? (
          <div className="ai-rail__empty">
            {conversations.length === 0 ? 'هنوز گفتگویی ندارید.' : 'گفتگویی پیدا نشد.'}
          </div>
        ) : (
          GROUPS.map((group) => {
            const items = visible.filter(group.match);
            if (items.length === 0) return null;
            return (
              <div key={group.id} className="ai-rail__group" role="group" aria-label={group.label}>
                <div className="ai-rail__group-label">{group.label}</div>
                {items.map((conversation) => (
                  <div
                    key={conversation.id}
                    className="ai-rail__item"
                    data-active={conversation.id === activeId}
                  >
                    <button
                      type="button"
                      className="ai-rail__item-main"
                      aria-current={conversation.id === activeId ? 'true' : undefined}
                      onClick={() => onSelect(conversation.id)}
                    >
                      <span className="ai-rail__item-title">{conversation.title}</span>
                      {/* Mobile list (M1 / M4) only: «مصرف N توکن» / «خواندنی». */}
                      {readOnly || conversation.usageUnknown || conversation.tokens > 0 ? (
                        <span className="ai-rail__item-usage">
                          {readOnly ? (
                            'خواندنی'
                          ) : conversation.usageUnknown ? (
                            'مصرف نامشخص'
                          ) : (
                            <>
                              مصرف <span className="sk-num">{faInt(conversation.tokens)}</span> توکن
                            </>
                          )}
                        </span>
                      ) : null}
                    </button>
                    <span className="ai-rail__item-menu">
                      <RowMenu
                        label={`عملیات گفتگو: ${conversation.title}`}
                        items={[
                          {
                            label: conversation.pinned ? 'برداشتن سنجاق' : 'سنجاق کردن',
                            onSelect: () => onTogglePin(conversation.id),
                          },
                          {
                            label: 'حذف از فهرست',
                            danger: true,
                            onSelect: () => onRemove(conversation.id),
                          },
                        ]}
                      />
                    </span>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>

      <div className="ai-rail__balance">
        <AssistantMenu
          base={menuBase}
          isTenantAdmin={isTenantAdmin}
          needsSetup={needsSetup}
          balance={tokens.balance}
          balanceLabel={balanceLabel}
        />
      </div>
    </aside>
  );
}
